import { useEffect, useState, useMemo, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { fetchAllRows } from '@/lib/supabaseHelpers';
import { Download, Search, RefreshCw, ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface Session {
  session_id: string;
  created_at: string;
  is_completed: boolean | null;
  device_type: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  ab_variant: string | null;
  total_duration_sec: number | null;
  back_clicks: number | null;
  scroll_depth: number | null;
  current_step: number | null;
}

interface Answer {
  session_id: string;
  question: string;
  answer: string;
}

interface LiveRawDataProps {
  dateFrom?: Date;
  dateTo?: Date;
}

type SortKey = 'created_at' | 'is_completed' | 'device_type' | 'utm_source' | 'utm_campaign' | 'ab_variant' | 'total_duration_sec' | 'current_step';

const PAGE_SIZE = 50;

const LiveRawData = ({ dateFrom, dateTo }: LiveRawDataProps) => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('created_at');
  const [sortAsc, setSortAsc] = useState(false);
  const [onlyCompleted, setOnlyCompleted] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sess, ans] = await Promise.all([
        fetchAllRows<Session>(
          'quiz_sessions',
          'session_id, created_at, is_completed, device_type, utm_source, utm_medium, utm_campaign, ab_variant, total_duration_sec, back_clicks, scroll_depth, current_step',
          [{ column: 'utm_source', operator: 'not.is', value: null }]
        ),
        fetchAllRows<Answer>('quiz_answers', 'session_id, question, answer'),
      ]);
      setSessions(sess);
      setAnswers(ans);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const channel = supabase
      .channel('liverawdata-sessions')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'quiz_sessions' }, (payload) => {
        const row = payload.new as Session;
        if (row.utm_source) {
          setSessions(prev => [row, ...prev]);
        }
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'quiz_sessions' }, (payload) => {
        const row = payload.new as Session;
        setSessions(prev => prev.map(s => s.session_id === row.session_id ? row : s));
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'quiz_answers' }, (payload) => {
        const row = payload.new as Answer;
        setAnswers(prev => [...prev, row]);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [fetchData]);

  // Build answer map: session_id -> { question -> answer }
  const answerMap = useMemo(() => {
    const map = new Map<string, Record<string, string>>();
    answers.forEach(a => {
      if (!map.has(a.session_id)) map.set(a.session_id, {});
      map.get(a.session_id)![a.question] = a.answer;
    });
    return map;
  }, [answers]);

  // Gather all unique questions (sorted by frequency)
  const allQuestions = useMemo(() => {
    const counts = new Map<string, number>();
    answers.forEach(a => counts.set(a.question, (counts.get(a.question) || 0) + 1));
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).map(([q]) => q);
  }, [answers]);

  // Filter by date + completed toggle + search
  const filtered = useMemo(() => {
    let list = sessions;

    if (onlyCompleted) {
      list = list.filter(s => s.is_completed === true);
    }

    if (dateFrom || dateTo) {
      list = list.filter(s => {
        const d = new Date(s.created_at);
        if (dateFrom && d < new Date(dateFrom.setHours(0, 0, 0, 0))) return false;
        if (dateTo) {
          const end = new Date(dateTo);
          end.setHours(23, 59, 59, 999);
          if (d > end) return false;
        }
        return true;
      });
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(s => {
        if (
          s.session_id.toLowerCase().includes(q) ||
          (s.utm_source || '').toLowerCase().includes(q) ||
          (s.utm_campaign || '').toLowerCase().includes(q) ||
          (s.device_type || '').toLowerCase().includes(q) ||
          (s.ab_variant || '').toLowerCase().includes(q)
        ) return true;
        // Also search in answers
        const ans = answerMap.get(s.session_id);
        if (ans) {
          return Object.values(ans).some(v => v.toLowerCase().includes(q));
        }
        return false;
      });
    }

    return list;
  }, [sessions, search, dateFrom, dateTo, answerMap, onlyCompleted]);

  // Sort
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const va = a[sortKey] ?? '';
      const vb = b[sortKey] ?? '';
      if (typeof va === 'boolean' && typeof vb === 'boolean') {
        return sortAsc ? Number(va) - Number(vb) : Number(vb) - Number(va);
      }
      if (typeof va === 'number' && typeof vb === 'number') {
        return sortAsc ? va - vb : vb - va;
      }
      const sa = String(va);
      const sb = String(vb);
      return sortAsc ? sa.localeCompare(sb) : sb.localeCompare(sa);
    });
  }, [filtered, sortKey, sortAsc]);

  const paged = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(v => !v);
    else { setSortKey(key); setSortAsc(false); }
    setPage(0);
  };

  const SortIcon = ({ k }: { k: SortKey }) => {
    if (sortKey !== k) return null;
    return sortAsc ? <ChevronUp className="w-3 h-3 inline ml-0.5" /> : <ChevronDown className="w-3 h-3 inline ml-0.5" />;
  };

  const exportCsv = () => {
    const metaCols = ['Sessie ID', 'Datum', 'Voltooid', 'Stap', 'Apparaat', 'UTM Source', 'UTM Medium', 'UTM Campaign', 'A/B Variant', 'Duur (s)', 'Terug-kliks', 'Scroll %'];
    const headers = [...metaCols, ...allQuestions];
    const rows = sorted.map(s => {
      const ans = answerMap.get(s.session_id) || {};
      return [
        s.session_id,
        s.created_at ? new Date(s.created_at).toLocaleString('nl-NL') : '',
        s.is_completed ? 'Ja' : 'Nee',
        String(s.current_step ?? ''),
        s.device_type || '',
        s.utm_source || '',
        s.utm_medium || '',
        s.utm_campaign || '',
        s.ab_variant || '',
        String(s.total_duration_sec ?? ''),
        String(s.back_clicks ?? ''),
        String(s.scroll_depth ?? ''),
        ...allQuestions.map(q => ans[q] || ''),
      ];
    });

    const csv = [
      headers.map(h => `"${h}"`).join(','),
      ...rows.map(r => r.map(c => `"${(c || '').replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `quiz-sessies-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const completed = useMemo(() => sessions.filter(s => s.is_completed).length, [sessions]);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
        <div className="animate-spin w-6 h-6 border-2 border-gray-300 border-t-gray-800 rounded-full mx-auto mb-3" />
        <p className="text-sm text-gray-400">Sessies + antwoorden laden ({'>'}6000 rijen)…</p>
      </div>
    );
  }

  const colHeaders: { label: string; key: SortKey }[] = [
    { label: 'Datum', key: 'created_at' },
    { label: 'Status', key: 'is_completed' },
    { label: 'Stap', key: 'current_step' },
    { label: 'Apparaat', key: 'device_type' },
    { label: 'UTM Bron', key: 'utm_source' },
    { label: 'Campagne', key: 'utm_campaign' },
    { label: 'A/B', key: 'ab_variant' },
    { label: 'Duur', key: 'total_duration_sec' },
  ];

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Totaal sessies', value: sessions.length.toLocaleString('nl-NL') },
          { label: 'Voltooid', value: completed.toLocaleString('nl-NL') },
          { label: 'CVR', value: `${sessions.length > 0 ? Math.round((completed / sessions.length) * 100) : 0}%` },
          { label: 'Quiz antwoorden', value: answers.length.toLocaleString('nl-NL') },
        ].map(({ label, value }) => (
          <div key={label} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{value}</div>
            <div className="text-xs text-gray-500 mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {/* Table card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
        {/* Toolbar */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h3 className="font-bold text-gray-900">
              📋 Sessies{onlyCompleted ? ' · alleen voltooid' : ''}{dateFrom || dateTo ? ' · gefilterd op datum' : ''}: {filtered.length.toLocaleString('nl-NL')}
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">Klik op een rij om alle antwoorden te zien · Alleen betaald verkeer</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Voltooid toggle */}
            <button
              onClick={() => { setOnlyCompleted(v => !v); setPage(0); setExpandedId(null); }}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border font-medium transition-all ${
                onlyCompleted
                  ? 'bg-green-600 text-white border-green-600 shadow-sm'
                  : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${onlyCompleted ? 'bg-white' : 'bg-gray-300'}`} />
              Alleen voltooid
            </button>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Zoek sessie, bron, antwoord…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); setExpandedId(null); }}
                className="pl-9 h-9 w-52 text-sm"
              />
            </div>
            <Button variant="outline" size="sm" onClick={() => { fetchData(); setPage(0); }} className="text-xs gap-1 h-9">
              <RefreshCw className="w-3 h-3" /> Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={exportCsv} className="text-xs gap-1 h-9">
              <Download className="w-3 h-3" /> {onlyCompleted ? 'CSV (voltooid)' : 'CSV'}
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="w-6 py-2 px-2" />
                {colHeaders.map(({ label, key }) => (
                  <th
                    key={key}
                    onClick={() => handleSort(key)}
                    className="text-left py-2 px-3 font-medium text-gray-500 whitespace-nowrap cursor-pointer select-none hover:text-gray-900"
                  >
                    {label}<SortIcon k={key} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paged.length === 0 && (
                <tr>
                  <td colSpan={9} className="py-8 text-center text-gray-400 text-sm">Geen sessies gevonden</td>
                </tr>
              )}
              {paged.map((s) => {
                const isExpanded = expandedId === s.session_id;
                const ans = answerMap.get(s.session_id);
                const hasAnswers = ans && Object.keys(ans).length > 0;
                return [
                  <tr
                    key={s.session_id}
                    onClick={() => setExpandedId(isExpanded ? null : s.session_id)}
                    className={`border-b border-gray-50 cursor-pointer transition-colors ${isExpanded ? 'bg-blue-50/60' : 'hover:bg-gray-50/70'}`}
                  >
                    <td className="py-2 px-2 text-gray-300">
                      {isExpanded
                        ? <ChevronDown className="w-3.5 h-3.5 text-blue-500" />
                        : <ChevronRight className="w-3.5 h-3.5" />
                      }
                    </td>
                    <td className="py-2 px-3 text-gray-700 whitespace-nowrap">
                      {s.created_at
                        ? new Date(s.created_at).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
                        : '—'}
                    </td>
                    <td className="py-2 px-3">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${s.is_completed ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {s.is_completed ? '✓ Voltooid' : `Stap ${s.current_step ?? '?'}`}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-gray-600">{s.current_step ?? '—'}</td>
                    <td className="py-2 px-3 text-gray-600 capitalize">{s.device_type || '—'}</td>
                    <td className="py-2 px-3 text-gray-600">{s.utm_source || '—'}</td>
                    <td className="py-2 px-3 text-gray-600 max-w-[180px] truncate" title={s.utm_campaign || ''}>{s.utm_campaign || '—'}</td>
                    <td className="py-2 px-3">
                      {s.ab_variant && (
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${s.ab_variant === 'A' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                          {s.ab_variant}
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-gray-600">{s.total_duration_sec ? `${s.total_duration_sec}s` : '—'}</td>
                  </tr>,
                  isExpanded && (
                    <tr key={`${s.session_id}-expanded`} className="bg-blue-50/40">
                      <td colSpan={9} className="py-3 px-6 pb-4">
                        <div className="text-[10px] text-gray-400 mb-2 font-medium uppercase tracking-wide">
                          Sessie-ID: {s.session_id} · back-kliks: {s.back_clicks ?? 0} · scroll: {s.scroll_depth ?? 0}%
                        </div>
                        {hasAnswers ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                            {Object.entries(ans!).map(([question, answer]) => (
                              <div key={question} className="bg-white rounded-lg border border-blue-100 p-2.5">
                                <div className="text-[10px] text-gray-400 font-medium mb-0.5 truncate" title={question}>{question}</div>
                                <div className="text-xs text-gray-800 font-medium">{answer || '—'}</div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400 italic">Geen antwoorden opgeslagen voor deze sessie</p>
                        )}
                      </td>
                    </tr>
                  ),
                ];
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
            <span className="text-xs text-gray-400">
              {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, sorted.length)} van {sorted.length.toLocaleString('nl-NL')} sessies
            </span>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" onClick={() => { setPage(0); setExpandedId(null); }} disabled={page === 0} className="text-xs h-7 px-2">«</Button>
              <Button variant="outline" size="sm" onClick={() => { setPage(p => p - 1); setExpandedId(null); }} disabled={page === 0} className="text-xs h-7">Vorige</Button>
              <span className="flex items-center px-2 text-xs text-gray-500">{page + 1} / {totalPages}</span>
              <Button variant="outline" size="sm" onClick={() => { setPage(p => p + 1); setExpandedId(null); }} disabled={page >= totalPages - 1} className="text-xs h-7">Volgende</Button>
              <Button variant="outline" size="sm" onClick={() => { setPage(totalPages - 1); setExpandedId(null); }} disabled={page >= totalPages - 1} className="text-xs h-7 px-2">»</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveRawData;
